import {
  ShareScreenBase,
  SKIP_WAITLIST_TITLE,
  skipWaitlistSuccessMessage,
} from "./share-screen-base"
import { getDeeplinkUrl } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { brandAppName, isRambleBrand } from "~/common/brand"
import { Logo } from "~/landing/components/logo"
import rambleMarkWhite from "~/images/brand/ramble-mark-white"
import royaleLineup from "~/images/share/royale-lineup"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { AppText } from "~/ui/app-text"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useRoomShareEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS, SHARE_PAGES } from "~/common/analytics"
import { getInviteRoomIds, getInviterData } from "~/share/share-embedded-data"
import { useScreenTracking } from "~/hooks/use-screen-tracking"

const EVENT_TITLE = "Battle Royale: Survive"

export const RoyaleScreen = (): JSX.Element => {
  const appName = brandAppName()
  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getDeeplinkUrl(appEnvironment, "home")
  const collectEmail = useCollectEmail()
  const phoneSubmission = useRoomSharePhoneSubmission()
  const emailSubmission = useRoomShareEmailSubmission()
  useDocumentTitleDescription(
    `${EVENT_TITLE} · ${appName}`,
    `A $10,000 Survivor-style game for charity with 20 reality stars. Thursday, October 15, 4-8 pm ET, streaming live on ${appName} + YouTube.`
  )
  useScreenTracking(SCREENS.Share, {
    share_page: SHARE_PAGES.Royale,
    deeplink_url: deeplinkUrl,
  })

  const onJoinPress = useCallback(async () => {
    const successMessage = skipWaitlistSuccessMessage(collectEmail)
    const successTitle = SKIP_WAITLIST_TITLE
    const inviterData = getInviterData()
    const [roomId, ...additionalRoomIds] = getInviteRoomIds()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      share_page: SHARE_PAGES.Royale,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      deeplink_url: deeplinkUrl,
    }

    const params = { successMessage, successTitle, trackingData, roomId, additionalRoomIds }

    if (collectEmail) {
      await emailSubmission.submitEmail(params)
    } else {
      await phoneSubmission.submitPhoneNumber(params)
    }
  }, [collectEmail, deeplinkUrl, emailSubmission, phoneSubmission])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      deeplinkUrl={deeplinkUrl}
      onJoinPress={onJoinPress}
      hideHeader
      {...inputProps}
      renderContent={() => (
        <div className="flex w-full max-w-[640px] flex-col items-center gap-6 px-4 pb-4 pt-5 text-center md:pb-0 md:pt-6">
          <div className="flex items-center gap-2">
            <Logo
              src={isRambleBrand() ? rambleMarkWhite.src : undefined}
              alt={appName}
              className="h-[22px] md:h-[24px]"
              analyticsProps={{ share_page: SHARE_PAGES.Royale }}
            />
            <AppText variant="h3" className="text-[18px] text-base-white">
              {appName} + RHAP Present
            </AppText>
          </div>

          <div className="flex flex-col items-center gap-2">
            <AppText
              variant="display"
              as="h1"
              className="text-[36px] leading-[38px] text-base-white max-md:tracking-2 md:text-[56px] md:leading-[56px]"
            >
              {EVENT_TITLE}
            </AppText>
            <AppText variant="body1-medium" className="text-[17px] text-base-white md:text-[18px]">
              $10,000 Survivor-style game for charity
            </AppText>
          </div>

          <div className="flex flex-col items-center gap-1">
            <AppText
              variant="h2"
              className="text-[27px] leading-[30px] text-base-yellow md:text-[31px] md:leading-[34px]"
            >
              Thursday, Oct 15 · 4–8 pm ET
            </AppText>
            <AppText variant="body1-medium" className="text-[17px] text-base-white md:text-[18px]">
              Join {appName} below to watch LIVE and help your favorite player win
            </AppText>
          </div>

          <img
            src={royaleLineup.src}
            width={royaleLineup.width}
            height={royaleLineup.height}
            alt="The 20 players: Big Brother's Andy, Big D, Brendon, Chelsie, Drew, Josh, Melody, Morgan, Rachel and Tucker; Survivor's Abi-Maria, Christian, Emily, Ethan, Omar, Reem, Sam, Sophi B, Tiffany and Tyson; hosted by Rob Cesternino"
            className="w-full rounded-2xl md:max-w-[480px]"
          />
        </div>
      )}
    />
  )
}

export default RoyaleScreen
